import { createClient } from 'contentful';
import { createClient as createManagementClient } from 'contentful-management';
import { sampleArticles } from './sample-articles';

// Utility function to sanitize environment variables by trimming whitespace and removing invalid characters
const sanitizeEnvVar = (value: string | undefined): string => {
  if (!value) return '';
  // Trim whitespace and remove any non-printable characters that could cause header issues
  // Only remove control characters, not printable ASCII characters
  const sanitized = value.trim().replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  
  // Add debug logging to see what's happening in production
  if (process.env.NODE_ENV === 'production') {
    console.log(`Sanitizing env var: original length=${value.length}, sanitized length=${sanitized.length}`);
    console.log(`Original starts with: ${value.substring(0, 10)}...`);
    console.log(`Sanitized starts with: ${sanitized.substring(0, 10)}...`);
  }
  
  return sanitized;
};

// Utility function to properly format Contentful image URLs
export const formatContentfulImageUrl = (url: string | undefined): string => {
  if (!url) return '';
  
  // Contentful URLs can come in different formats:
  // 1. Protocol-relative: //images.ctfassets.net/...
  // 2. Already absolute: https://images.ctfassets.net/...
  // 3. Relative: /images.ctfassets.net/... (shouldn't happen but handle it)
  
  if (url.startsWith('//')) {
    return `https:${url}`;
  } else if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  } else if (url.startsWith('/')) {
    return `https:${url}`;
  } else {
    // Assume it's already a proper URL or add https:// if needed
    return url.includes('://') ? url : `https://${url}`;
  }
};

// Create the Content Management client for administrative operations
// This client is used for administrative tasks like creating and updating entries
// Use lazy initialization to avoid errors when the management token is not available
let _contentfulManagementClient: any = null;

export const getContentfulManagementClient = () => {
  // Only initialize on server-side
  if (typeof window !== 'undefined') {
    throw new Error('Contentful Management Client cannot be used on client-side');
  }
  
  if (!_contentfulManagementClient) {
    const managementToken = sanitizeEnvVar(process.env.CONTENTFUL_MANAGEMENT_TOKEN);
    if (!managementToken) {
      console.warn('CONTENTFUL_MANAGEMENT_TOKEN is not set');
      return null;
    }
    try {
      _contentfulManagementClient = createManagementClient({
        accessToken: managementToken,
      });
    } catch (error) {
      console.error('Failed to create Contentful Management Client:', error);
      return null;
    }
  }
  return _contentfulManagementClient;
};

// Keep the old export for backward compatibility, but make it safe for client-side
export const contentfulManagementClient = {
  get spaces() {
    const client = getContentfulManagementClient();
    return client ? client.spaces : null;
  },
  get environments() {
    const client = getContentfulManagementClient();
    return client ? client.environments : null;
  }
};

// Define types for Contentful responses
export type ContentfulTechnology = {
  id?: string; // Added id field
  name: string;
  slug: string;
  category: string;
  description?: string;
  experienceLevel?: number; // Added experienceLevel field
  experienceYears?: number; // Added experienceYears field
  tags?: string[]; // Added tags field
  features?: any[]; // Added features field
  pros?: any[]; // Added pros field
  cons?: any[]; // Added cons field
  projects?: any[]; // Added projects field
  relatedTechnologies?: any[]; // Added relatedTechnologies field
  logo?: {
    fields: {
      file: {
        url: string;
      }
    }
  };
  website?: string;
};

// Define Category interface
export interface Category {
  slug: string;
  name: string;
}

export type ContentfulTechnologyUse = {
  technology: {
    sys: {
      id: string;
    };
    fields: ContentfulTechnology;
  };
  expertiseLevel: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
  description?: string;
};

export type ContentfulTestimonial = {
  quote: string;
  author: string;
  position: string;
};

export type ContentfulBrand = {
  name: string;
  slug: string;
  domain: string;
  description: string;
  detailedExperience: string | any; // Can be string or rich text object
  yearWorked: string;
  projectType: string;
  achievements: string[];
  logo?: {
    fields: {
      file: {
        url: string;
      }
    }
  };
  technologies?: {
    sys: {
      id: string;
    };
    fields: ContentfulTechnologyUse;
  }[];
  testimonial?: ContentfulTestimonial;
  relationshipType: 'direct' | 'agency';
  associatedRole?: string;
  associatedCompany?: string;
};

export type ContentfulExperience = {
  company: string; // Keep the old field for backward compatibility during migration
  companyRef?: { // New field that links to brand entries
    sys: {
      id: string;
    };
    fields: ContentfulBrand;
  };
  role: string;
  period: string;
  slug: string;
  description: string;
  responsibilities: string[];
  achievements: string[];
  skills: string[];
  logo?: {
    fields: {
      file: {
        url: string;
      }
    }
  };
  companyDomain?: string; // Adding this for favicon fallback
  technologies?: {
    sys: {
      id: string;
    };
    fields: ContentfulTechnologyUse;
  }[];
  relatedBrands?: {
    sys: {
      id: string;
    };
    fields: ContentfulBrand;
  }[];
};

// Define ContentfulArticle type based on README-contentful.md
export type ContentfulArticleAuthor = {
  name: string;
  avatar?: {
    fields: {
      file: {
        url: string;
      }
    }
  };
};

export type ContentfulArticle = {
  id?: string; // Add optional id field
  title: string;
  slug: string;
  excerpt: string;
  content: any; // Rich text, typically an object
  featuredImage?: {
    fields: {
      file: {
        url: string;
      },
      title?: string; // Title property for the image
    }
  };
  publishDate: string;
  tags?: string[];
  author?: {
    sys: {
      id: string;
    };
    fields: ContentfulArticleAuthor;
  };
};

// Create Contentful client
console.log('Attempting to initialize Contentful client...');

console.log('CONTENTFUL_SPACE_ID:', process.env.CONTENTFUL_SPACE_ID ? 'Set' : 'NOT SET');
console.log('CONTENTFUL_CONTENT_DELIVERY_API:', process.env.CONTENTFUL_CONTENT_DELIVERY_API ? 'Set (length: ' + process.env.CONTENTFUL_CONTENT_DELIVERY_API.length + ')' : 'NOT SET');
console.log('CONTENTFUL_ACCESS_TOKEN:', process.env.CONTENTFUL_ACCESS_TOKEN ? 'Set (length: ' + process.env.CONTENTFUL_ACCESS_TOKEN.length + ')' : 'NOT SET');
console.log('CONTENTFUL_ENVIRONMENT:', process.env.CONTENTFUL_ENVIRONMENT ? process.env.CONTENTFUL_ENVIRONMENT : 'NOT SET (defaulting to master)');

// Sanitize environment variables to prevent header issues in production
// Initialize Contentful client variables - only on server side
let _contentfulClient: any = null;

// Function to get or create the Contentful client
const getClient = () => {
  if (typeof window !== 'undefined') {
    // On client side, return a mock client to prevent errors
    return null;
  }

  if (!_contentfulClient) {
    const spaceId = sanitizeEnvVar(process.env.CONTENTFUL_SPACE_ID);
    const accessToken = sanitizeEnvVar(process.env.CONTENTFUL_ACCESS_TOKEN || process.env.CONTENTFUL_CONTENT_DELIVERY_API);
    const environment = sanitizeEnvVar(process.env.CONTENTFUL_ENVIRONMENT) || 'master';

    // Validation: Contentful tokens should be at least 20 characters long
    if (!spaceId || spaceId.length < 10) {
      console.warn('❌ Invalid CONTENTFUL_SPACE_ID - too short or missing');
      return null;
    }

    if (!accessToken || accessToken.length < 20) {
      console.warn('❌ Invalid Contentful access token - too short or missing');
      
      // Try without sanitization as fallback
      const fallbackToken = process.env.CONTENTFUL_ACCESS_TOKEN || process.env.CONTENTFUL_CONTENT_DELIVERY_API;
      if (fallbackToken && fallbackToken.length > 20) {
        console.log('🔄 Using unsanitized token as fallback');
        try {
          _contentfulClient = createClient({
            space: spaceId,
            accessToken: fallbackToken,
            environment: environment,
          });
        } catch (error) {
          console.error('Failed to create Contentful client:', error);
          return null;
        }
        return _contentfulClient;
      }
      return null;
    }

    try {
      _contentfulClient = createClient({
        space: spaceId,
        accessToken: accessToken,
        environment: environment,
      });
    } catch (error) {
      console.error('Failed to create Contentful client:', error);
      return null;
    }
  }

  return _contentfulClient;
};

// Create the client - will be null on client side
const client = getClient();

// Export the client for use in API routes
export { client as contentfulClient };

// Helper to parse Contentful response
const parseContentfulResponse = <T>(response: any): T => {
  if (!response) return {} as T;
  
  // Handle fields that are references to other content types (like technologies)
  const parsed = { ...response.fields };
  
  return parsed as T;
};

// Fetch all brands
export async function getAllBrands(): Promise<ContentfulBrand[]> {
  try {
    if (!client) {
      console.warn('Contentful client not available');
      return [];
    }
    
    const response = await client.getEntries({
      content_type: 'brand',
      include: 2, // Include linked entries (technologies)
    });
    
    return response.items.map((item: any) => parseContentfulResponse<ContentfulBrand>(item));
  } catch (error) {
    console.error('Error fetching brands from Contentful:', error);
    return [];
  }
}

// Fetch single brand by slug
export async function getBrandBySlug(slug: string): Promise<ContentfulBrand | null> {
  try {
    if (!client) {
      console.warn('Contentful client not available');
      return null;
    }

    const response = await client.getEntries({
      content_type: 'brand',
      'fields.slug': slug,
      include: 2, // Include linked entries (technologies)
      limit: 1,
    });
    
    if (response.items.length === 0) {
      return null;
    }
    
    return parseContentfulResponse<ContentfulBrand>(response.items[0]);
  } catch (error) {
    console.error(`Error fetching brand ${slug} from Contentful:`, error);
    return null;
  }
}

// Fetch all experiences
export async function getAllExperiences(): Promise<ContentfulExperience[]> {
  try {
    if (!client) {
      console.warn('Contentful client not available');
      return [];
    }
    
    const response = await client.getEntries({
      content_type: 'experience',
      include: 2, // Include linked entries (technologies, brands)
    });
    
    return response.items.map((item: any) => parseContentfulResponse<ContentfulExperience>(item));
  } catch (error) {
    console.error('Error fetching experiences from Contentful:', error);
    return [];
  }
}

// Fetch single experience by slug
export async function getExperienceBySlug(slug: string): Promise<ContentfulExperience | null> {
  try {
    if (!client) {
      console.warn('Contentful client not available');
      return null;
    }
    
    const response = await client.getEntries({
      content_type: 'experience',
      'fields.slug': slug,
      include: 2, // Include linked entries (technologies, brands)
      limit: 1,
    });
    
    if (response.items.length === 0) {
      return null;
    }
    
    return parseContentfulResponse<ContentfulExperience>(response.items[0]);
  } catch (error) {
    console.error(`Error fetching experience ${slug} from Contentful:`, error);
    return null;
  }
}

// Fetch all technologies
export async function getAllTechnologies(): Promise<ContentfulTechnology[]> {
  try {
    if (!client) {
      console.warn('Contentful client not available');
      return [];
    }
    
    const response = await client.getEntries({
      content_type: 'technology',
    });
    
    return response.items.map((item: any) => {
      const parsedItem = parseContentfulResponse<ContentfulTechnology>(item);
      // Add the id from sys.id
      parsedItem.id = item.sys.id;
      return parsedItem;
    });
  } catch (error) {
    console.error('Error fetching technologies from Contentful:', error);
    return [];
  }
}

// Fetch single technology by slug
export async function getTechnologyBySlug(slug: string): Promise<ContentfulTechnology | null> {
  try {
    if (!client) {
      console.warn('Contentful client not available');
      return null;
    }
    
    const response = await client.getEntries({
      content_type: 'technology',
      'fields.slug': slug,
      limit: 1,
    });
    
    if (response.items.length === 0) {
      return null;
    }
    
    return parseContentfulResponse<ContentfulTechnology>(response.items[0]);
  } catch (error) {
    console.error(`Error fetching technology ${slug} from Contentful:`, error);
    return null;
  }
}

// Get brands using a specific technology
export async function getBrandsByTechnology(techSlug: string): Promise<ContentfulBrand[]> {
  try {
    if (!client) {
      console.warn('Contentful client not available');
      return [];
    }
    
    // First get the technology ID
    const techResponse = await client.getEntries({
      content_type: 'technology',
      'fields.slug': techSlug,
      limit: 1,
    });
    
    if (techResponse.items.length === 0) {
      return [];
    }
    
    const techId = techResponse.items[0].sys.id;
    
    // Get technology uses that link to this technology
    const techUseResponse = await client.getEntries({
      content_type: 'technologyUse',
      'fields.technology.sys.id': techId,
    });
    
    if (techUseResponse.items.length === 0) {
      return [];
    }
    
    // Get tech use IDs
    const techUseIds = techUseResponse.items.map((item: any) => item.sys.id);
    
    // Get brands that link to any of these technology uses
    const brandsPromises = techUseIds.map((id: string) => 
      client.getEntries({
        content_type: 'brand',
        'fields.technologies.sys.id': id,
        include: 2,
      })
    );
    
    const brandsResponses = await Promise.all(brandsPromises);
    
    // Combine and deduplicate results
    const brands = new Map();
    brandsResponses.forEach(response => {
      response.items.forEach((item: any) => {
        if (!brands.has(item.sys.id)) {
          brands.set(item.sys.id, parseContentfulResponse<ContentfulBrand>(item));
        }
      });
    });
    
    return Array.from(brands.values());
  } catch (error) {
    console.error(`Error fetching brands for technology ${techSlug}:`, error);
    return [];
  }
}

// Get experiences using a specific technology
export async function getExperiencesByTechnology(techSlug: string): Promise<ContentfulExperience[]> {
  try {
    if (!client) {
      console.warn('Contentful client not available');
      return [];
    }
    
    // First get the technology ID
    const techResponse = await client.getEntries({
      content_type: 'technology',
      'fields.slug': techSlug,
      limit: 1,
    });
    
    if (techResponse.items.length === 0) {
      return [];
    }
    
    const techId = techResponse.items[0].sys.id;
    
    // Get technology uses that link to this technology
    const techUseResponse = await client.getEntries({
      content_type: 'technologyUse',
      'fields.technology.sys.id': techId,
    });
    
    if (techUseResponse.items.length === 0) {
      return [];
    }
    
    // Get tech use IDs
    const techUseIds = techUseResponse.items.map((item: any) => item.sys.id);
    
    // Get experiences that link to any of these technology uses
    const experiencesPromises = techUseIds.map((id: string) => 
      client.getEntries({
        content_type: 'experience',
        'fields.technologies.sys.id': id,
        include: 2,
      })
    );
    
    const experiencesResponses = await Promise.all(experiencesPromises);
    
    // Combine and deduplicate results
    const experiences = new Map();
    experiencesResponses.forEach(response => {
      response.items.forEach((item: any) => {
        if (!experiences.has(item.sys.id)) {
          experiences.set(item.sys.id, parseContentfulResponse<ContentfulExperience>(item));
        }
      });
    });
    
    return Array.from(experiences.values());
  } catch (error) {
    console.error(`Error fetching experiences for technology ${techSlug}:`, error);
    return [];
  }
}

// Technology adapter function to match the format expected by tech-stack components
export function adaptContentfulTechnology(contentfulTech: ContentfulTechnology): any {
  // Prepare technology slug for icon services
  const techSlug = contentfulTech.slug || '';
  
  // Determine the logo URL with multiple fallbacks
  let logoUrl = '';
  
  // 1. First priority: Use logo from Contentful if available
  if (contentfulTech.logo?.fields?.file?.url) {
    logoUrl = formatContentfulImageUrl(contentfulTech.logo.fields.file.url);
  }
  // 2. Second priority: Try DevIcon CDN with original style
  else {
    // Try to map common tech names to their DevIcon equivalents
    const devIconSlug = getTechDevIconMapping(techSlug);
    logoUrl = `https://cdn.jsdelivr.net/gh/devicons/devicon/icons/${devIconSlug}/${devIconSlug}-original.svg`;
    
    // 3. Third priority: Try DevIcon with plain style if original doesn't exist
    // This will be handled client-side with error handling on the Image component
  }
  
  return {
    id: contentfulTech.id || contentfulTech.slug, // Use slug as fallback ID
    name: contentfulTech.name,
    slug: contentfulTech.slug,
    description: contentfulTech.description || '',
    category: contentfulTech.category,
    logo: logoUrl,
    // Include additional fallback URLs that can be tried if the main logo fails
    logoFallbacks: [
      // DevIcon plain style as fallback
      `https://cdn.jsdelivr.net/gh/devicons/devicon/icons/${techSlug}/${techSlug}-plain.svg`,
      // Use Google's favicon service as a last resort
      `https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${techSlug}.com&size=128`,
      // SimpleIcons as another option
      `https://cdn.simpleicons.org/${techSlug}`
    ],
    url: contentfulTech.website || `https://${techSlug}.com`,
    experience: contentfulTech.experienceYears || 0,
    detailedDescription: contentfulTech.description,
    features: contentfulTech.features || [],
    pros: contentfulTech.pros || [],
    cons: contentfulTech.cons || [],
    projects: contentfulTech.projects || [],
    relatedTechnologies: contentfulTech.relatedTechnologies || [],
    tags: contentfulTech.tags || [],
  };
}

// Helper function to map technology slugs to their DevIcon equivalents
function getTechDevIconMapping(slug: string): string {
  // Common mappings for technologies that have different names in DevIcon
  const mappings: Record<string, string> = {
    'javascript': 'javascript',
    'typescript': 'typescript',
    'react': 'react',
    'nextjs': 'nextjs',
    'nodejs': 'nodejs',
    'python': 'python',
    'django': 'django',
    'postgresql': 'postgresql',
    'mysql': 'mysql',
    'mongodb': 'mongodb',
    'aws': 'amazonwebservices',
    'googlecloud': 'googlecloud',
    'firebase': 'firebase',
    'docker': 'docker',
    'kubernetes': 'kubernetes',
    'git': 'git',
    'github': 'github',
    'jira': 'jira',
    'figma': 'figma',
    'html5': 'html5',
    'css3': 'css3',
    'sass': 'sass',
    'tailwindcss': 'tailwindcss',
    'bootstrap': 'bootstrap',
    'redux': 'redux',
    'graphql': 'graphql',
    'php': 'php',
    'laravel': 'laravel',
    'vue': 'vuejs',
    'angular': 'angularjs',
    'webpack': 'webpack'
    // Add more mappings as needed
  };
  
  return mappings[slug.toLowerCase()] || slug;
}

// Get technologies by category from Contentful
export async function getContentfulTechnologiesByCategory(category?: string): Promise<Record<string, any[]>> {
  try {
    const technologies = await getAllTechnologies();
    
    // Group by category
    const categorized: Record<string, any[]> = {};
    
    technologies.forEach(tech => {
      // Skip if a specific category was requested and this tech doesn't match
      if (category && tech.category !== category) {
        return;
      }
      
      const adaptedTech = adaptContentfulTechnology(tech);
      if (!categorized[tech.category]) {
        categorized[tech.category] = [];
      }
      categorized[tech.category].push(adaptedTech);
    });
    
    return categorized;
  } catch (error) {
    console.error('Error getting technologies by category from Contentful:', error);
    return {};
  }
}

// Get technologies for a specific category
export async function getTechnologiesByCategory(category: string): Promise<any[]> {
  try {
    if (!client) {
      console.warn('Contentful client not available');
      return [];
    }
    
    const response = await client.getEntries({
      content_type: 'technology',
      'fields.category': category,
    });
    
    return response.items.map((item: any) => {
      const tech = parseContentfulResponse<ContentfulTechnology>(item);
      // Add the id from sys.id and adapt to the expected format
      return adaptContentfulTechnology({
        ...tech,
        id: item.sys.id
      });
    });
  } catch (error) {
    console.error(`Error fetching technologies for category ${category}:`, error);
    return [];
  }
}

// Get all unique technology categories
export async function getCategories(): Promise<Category[]> {
  try {
    const technologies = await getAllTechnologies();
    
    // Extract unique categories from technologies
    const uniqueCategories = new Map<string, Category>();
    
    technologies.forEach(tech => {
      if (tech.category) {
        const slug = tech.category.toLowerCase().replace(/\s+/g, '-');
        uniqueCategories.set(slug, {
          name: tech.category,
          slug: slug
        });
      }
    });
    
    return Array.from(uniqueCategories.values());
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
}

// Fetch all articles
export async function getAllArticles(): Promise<ContentfulArticle[]> {
  try {
    // Check if client is available
    if (!client) {
      console.warn('Contentful client not available, using sample articles for demo');
      return sampleArticles;
    }

    const response = await client.getEntries({
      content_type: 'article',
      include: 2, // Include linked entries (author)
      order: ['-fields.publishDate'], // Sort by publish date descending
    });
    
    return response.items.map((item: any) => {
      const parsedItem = parseContentfulResponse<ContentfulArticle>(item);
      // Add the id from sys.id
      return {
        ...parsedItem,
        id: item.sys.id
      };
    });
  } catch (error) {
    console.error('Error fetching articles from Contentful:', error);
    console.log('Falling back to sample articles for demo');
    return sampleArticles;
  }
}

// Fetch single article by slug
export async function getArticleBySlug(slug: string): Promise<ContentfulArticle | null> {
  try {
    if (!client) {
      console.warn('Contentful client not available');
      return null;
    }
    
    const response = await client.getEntries({
      content_type: 'article',
      'fields.slug': slug,
      include: 2, // Include linked entries (author)
      limit: 1,
    });
    
    if (response.items.length === 0) {
      return null;
    }
    
    const item = response.items[0];
    const parsedItem = parseContentfulResponse<ContentfulArticle>(item);
    
    return {
      ...parsedItem,
      id: item.sys.id
    };
  } catch (error) {
    console.error(`Error fetching article ${slug} from Contentful:`, error);
    return null;
  }
}

// Get category by slug
export async function getCategoryBySlug(slug: string): Promise<Category | null> {
  try {
    const categories = await getCategories();
    return categories.find(category => category.slug === slug) || null;
  } catch (error) {
    console.error(`Error getting category with slug ${slug}:`, error);
    return null;
  }
}

// Helper function to format date
export function formatDate(dateString: string) {
  const options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  };
  return new Date(dateString).toLocaleDateString('en-US', options);
}